export type MarkPlacement = "end" | "beginning" | "above" | "below" | ""

export type KitchenGroup = {
  name: string
  amount: number
  price: number
  marks: string[]
  itemNote: string
  seatNote: string
  options: string[]
  category: string
  item: any
}

export type WaiterLine = {
  amount: number
  name: string
  itemNote: string
  options: string[]
  marks: string[]
}

export type WaiterSeat = {
  mark: string
  seatNote: string
  items: WaiterLine[]
}

export function noteDisplay(raw: unknown): string {
  if (raw == null || raw === "") return ""
  const text = String(raw)
  try {
    const parsed = JSON.parse(text)
    if (parsed && typeof parsed === "object") {
      const parts: string[] = []
      if (Array.isArray(parsed.allergies) && parsed.allergies.length) {
        parts.push(parsed.allergies.join(", "))
      }
      const notes = String(parsed.notes || "").trim()
      if (notes) parts.push(notes)
      return parts.join("\n")
    }
  } catch {
    return text
  }
  return text
}

export function seatMark(tableName: string, seatNumber: unknown): string {
  const table = String(tableName || "").trim()
  const seat = Number(seatNumber)
  if (seat > 0 && table) return `${table}-${seat}`
  if (table) return table
  if (seat > 0) return String(seat)
  return ""
}

export function seatTicketMark(seatNumber: unknown): string {
  const seat = Number(seatNumber)
  return Number.isInteger(seat) && seat > 0 ? `S${seat}` : ""
}

export function optionLines(item: any, withPrice: boolean): string[] {
  const lines: string[] = []
  const groups = item?.advancedOptions?.selections
  if (!Array.isArray(groups)) return lines
  groups.forEach((group: any) => {
    const optionsMap = group?.optionSet?.options
      ? new Map(group.optionSet.options.map((opt: any) => [opt.name, opt]))
      : new Map()
    Object.entries(group?.selections || {}).forEach(([name, opt]: [string, any]) => {
      if (!opt || opt.userQuantity <= 0) return
      const displayName = String((optionsMap.get(name) as any)?.printName || "").trim() || name
      let text = opt.userQuantity > 1 ? `${opt.userQuantity} x ${displayName}` : displayName
      if (withPrice && opt.price > 0) text += ` $${Number(opt.total || 0).toFixed(2)}`
      lines.push(text)
    })
  })
  return lines
}

function identity(item: any): string {
  const selected = item?.selectedOption?.name || (typeof item?.selectedOption === "string" ? item.selectedOption : "")
  const course = Number(item?.serviceCourse)
  const courseMark = Number.isInteger(course) && course >= 1 && course <= 10 ? `|c${course}` : ""
  return `${item?.id || ""}|${selected}|${JSON.stringify(item?.advancedOptions || null)}${courseMark}`
}

function kitchenLineAmount(copies: any[]): number {
  const splitCopy = copies.find((item) => item?.splitInfo?.splitMethod === "equal" || item?.splitInfo?.splitMethod === "shared")
  const original = Number(splitCopy?.splitInfo?.originalQuantity)
  if (splitCopy && Number.isFinite(original) && original > 0) return original
  const sum = copies.reduce((total, item) => total + Number(item.amount ?? item.qty ?? 1), 0)
  const nearest = Math.round(sum)
  if (nearest >= 0 && Math.abs(sum - nearest) < 0.001) return nearest
  return Math.round(sum * 100) / 100
}

function toGroup(item: any, tableName: string, withMarks: boolean): KitchenGroup {
  return {
    name: item.printName || item.name || "",
    amount: Number(item.amount ?? item.qty ?? 1),
    price: Number(item.price || 0),
    marks: withMarks ? [seatTicketMark(item.seatNumber)].filter(Boolean) : [],
    itemNote: String(item.itemNote || "").trim(),
    seatNote: withMarks ? noteDisplay(item.seatNote).replace(/\n/g, " ") : "",
    options: optionLines(item, false),
    category: item.category || "Uncategorized",
    item,
  }
}

export function groupKitchenItems(items: any[], tableName: string, isTable: boolean): KitchenGroup[] {
  if (!isTable) return (items || []).map((item) => toGroup(item, tableName, false))
  const groups = new Map<string, KitchenGroup>()
  const members = new Map<string, any[]>()
  const order: string[] = []
  ;(items || []).forEach((item) => {
    const itemNote = String(item.itemNote || "").trim()
    const seatNote = noteDisplay(item.seatNote).replace(/\n/g, " ")
    const plain = !itemNote && !seatNote
    const key = plain
      ? `plain|${identity(item)}`
      : `special|${identity(item)}|${itemNote}|${seatNote}`
    if (!groups.has(key)) {
      groups.set(key, {
        name: item.printName || item.name || "",
        amount: 0,
        price: Number(item.price || 0),
        marks: [],
        itemNote,
        seatNote,
        options: optionLines(item, false),
        category: item.category || "Uncategorized",
        item,
      })
      members.set(key, [])
      order.push(key)
    }
    members.get(key)!.push(item)
    const mark = seatTicketMark(item.seatNumber)
    const group = groups.get(key)!
    if (mark && !group.marks.includes(mark)) group.marks.push(mark)
  })
  return order.map((key) => {
    const group = groups.get(key)!
    group.amount = kitchenLineAmount(members.get(key) || [])
    return group
  })
}

export function groupByCategory(groups: KitchenGroup[]): Array<[string, KitchenGroup[]]> {
  const map = new Map<string, KitchenGroup[]>()
  groups.forEach((group) => {
    const category = group.category || "Uncategorized"
    if (!map.has(category)) map.set(category, [])
    map.get(category)!.push(group)
  })
  return Array.from(map.entries())
}

export function waiterSeats(items: any[], tableName: string): WaiterSeat[] {
  const buckets = new Map<number, any[]>()
  const order: number[] = []
  ;(items || []).forEach((item) => {
    const seat = Number(item.seatNumber) > 0 ? Number(item.seatNumber) : 0
    if (!buckets.has(seat)) {
      buckets.set(seat, [])
      order.push(seat)
    }
    buckets.get(seat)!.push(item)
  })
  return order.map((seat) => {
    const raw = buckets.get(seat) || []
    const lines = new Map<string, WaiterLine>()
    const lineOrder: string[] = []
    raw.forEach((item) => {
      const itemNote = String(item.itemNote || "").trim()
      const key = `${identity(item)}|${itemNote}`
      if (!lines.has(key)) {
        lines.set(key, {
          amount: 0,
          name: item.printName || item.name || "",
          itemNote,
          options: optionLines(item, false),
          marks: [],
        })
        lineOrder.push(key)
      }
      const line = lines.get(key)!
      line.amount += Number(item.amount ?? 1)
      const mark = seatMark(tableName, item.seatNumber)
      if (mark && !line.marks.includes(mark)) line.marks.push(mark)
    })
    return {
      mark: seatMark(tableName, seat),
      seatNote: noteDisplay(raw.find((item) => item.seatNote)?.seatNote).replace(/\n/g, " "),
      items: lineOrder.map((key) => lines.get(key)!),
    }
  })
}

export function markPlacement(value: unknown): MarkPlacement {
  if (value === "beginning" || value === "above" || value === "below" || value === "end") return value
  return ""
}

export function kitchenMarkPlacement(value: unknown, hasMarks: boolean): MarkPlacement {
  const chosen = markPlacement(value)
  if (chosen) return chosen
  return hasMarks ? "end" : ""
}
